import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { raceChip, personalityChip } from '../../utils/dossierFlavor';
import { DossierFlavorChip } from '../dossier/DossierFlavorChip';
import { deriveHeadlineStats } from '../../utils/groupIntelByTarget';
import { parseIntelData } from '../../types/intel';
import type { IntelReportDto, CrystalBallData, SpyOnMilitaryData, SurveyData, InfiltrateData, SpyOnScienceData, SpyOnHomeDefenseData, SpyOnThroneData } from '../../types/intel';
import type { TargetGroup } from '../../utils/groupIntelByTarget';
import { MobileCrystalBallCard } from '../targetFinder/MobileCrystalBallCard';
import { MobileSpyOnMilitaryCard } from '../targetFinder/MobileSpyOnMilitaryCard';
import { MobileSurveyCard } from '../targetFinder/MobileSurveyCard';
import { MobileInfiltrateCard, MobileSpyOnScienceCard, MobileSpyOnHomeDefenseCard, MobileSpyOnThroneCard } from './MobileIntelDetailCards';
import { ConfirmModal } from '../ConfirmModal';
import { useToast } from '../../hooks/useToast';

const READ_ONLY_STACK_CAP = 5;

interface MobileIntelDetailScreenProps {
  worldId: string;
  provinceId: string;
  targetProvinceId: string;
  targetGroups: TargetGroup[];
  loading: boolean;
  error: string | null;
  onBack: () => void;
  handleShare: (reportId: string) => void;
  handleDelete: (reportId: string) => void;
  refetchTarget: (targetProvinceId: string) => void;
}

function byFreshest(reports: IntelReportDto[]): IntelReportDto[] {
  return [...reports].sort((a, b) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt));
}

/**
 * SF-157 Component 5: renders when `MobileIntelHubShell`'s `selectedTargetId` is set. Looks up its
 * group via `targetGroups.find(...)` every render (not a snapshot prop) and returns `null` for the
 * one frame that lookup fails — the actual navigation back to the list is owned exclusively by
 * `MobileIntelHubShell` (its dangling-target guard).
 */
export function MobileIntelDetailScreen({
  worldId, provinceId, targetProvinceId, targetGroups, loading, error, onBack, handleShare, handleDelete, refetchTarget,
}: MobileIntelDetailScreenProps) {
  const { addToast } = useToast();
  const headerRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const pendingDeleteFocusRef = useRef(false);

  const group = targetGroups.find((g) => g.targetProvinceId === targetProvinceId);

  useEffect(() => {
    headerRef.current?.focus();
  }, []);

  // Post-Confirm focus: the triggering Delete icon has just been unmounted. Move focus to the
  // next remaining Delete icon for this target if one exists, else to this screen's own heading.
  useEffect(() => {
    if (!pendingDeleteFocusRef.current) return;
    pendingDeleteFocusRef.current = false;
    const nextDelete = rootRef.current?.querySelector<HTMLButtonElement>('button[aria-label^="Delete this"]');
    if (nextDelete) {
      nextDelete.focus();
    } else {
      headerRef.current?.focus();
    }
  }, [group]);

  if (!group) return null;

  const onResult = (message: string, type: 'success' | 'failure' | 'warning') => addToast(message, type);
  const onGathered = () => refetchTarget(targetProvinceId);

  function requestDelete(reportId: string) {
    setPendingDeleteId(reportId);
  }

  function confirmDelete() {
    if (pendingDeleteId) {
      pendingDeleteFocusRef.current = true;
      handleDelete(pendingDeleteId);
    }
    setPendingDeleteId(null);
  }

  function toggleExpanded(type: string) {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  }

  const stats = deriveHeadlineStats(group);
  const hasCb = group.reports.some((r) => r.type === 'CB');

  const byType = (type: IntelReportDto['type']) => byFreshest(group!.reports.filter((r) => r.type === type));

  const cbReports = byType('CB');
  const somReports = byType('SOM');
  const surveyReports = byType('SURVEY');
  const infiltrateReports = byType('INFILTRATE');
  const sosReports = byType('SOS');
  const sohdReports = byType('SOHD');
  const sotReports = byType('SOT');

  function stackedProps(reports: IntelReportDto[], gatherType: 'CB' | 'SOM' | 'SURVEY') {
    const [freshest, ...older] = reports;
    const expanded = expandedTypes.has(gatherType);
    const visibleOlder = expanded ? older : older.slice(0, READ_ONLY_STACK_CAP);
    const hiddenCount = older.length - visibleOlder.length;
    return { freshest, visibleOlder, hiddenCount };
  }

  function showNMore(gatherType: 'CB' | 'SOM' | 'SURVEY', hiddenCount: number) {
    if (hiddenCount <= 0) return null;
    return (
      <button
        type="button"
        onClick={() => toggleExpanded(gatherType)}
        style={{ width: '100%', minHeight: 36, background: 'none', border: `1px dashed ${COLORS.border}`, borderRadius: 6, color: COLORS.text.tertiary, fontSize: 12, cursor: 'pointer', marginBottom: 10 }}
      >
        Show {hiddenCount} more
      </button>
    );
  }

  return (
    <div ref={rootRef} style={{ padding: 16 }}>
      <button
        type="button"
        onClick={onBack}
        aria-label="Back to results"
        style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.link, fontSize: 14, cursor: 'pointer', padding: '4px 0' }}
      >
        ← Back
      </button>

      <h2 ref={headerRef} tabIndex={-1} style={{ margin: '4px 0 2px', color: COLORS.text.primary, fontSize: 18, fontWeight: 700, outline: 'none' }}>
        {group.targetProvinceName}{' '}
        <span style={{ color: COLORS.link, fontSize: 13, fontWeight: 400 }}>({group.targetKingdomLocation})</span>
      </h2>

      {group.race != null && (
        <div style={{ display: 'flex', gap: 4, margin: '4px 0' }}>
          <DossierFlavorChip meta={raceChip(group.race)} />
          {hasCb && group.personality != null && <DossierFlavorChip meta={personalityChip(group.personality)} />}
        </div>
      )}

      {stats.networth != null && (
        <div style={{ color: COLORS.gold, fontSize: 15, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", margin: '4px 0 12px' }}>
          Networth {formatNumber(stats.networth)}
        </div>
      )}

      {cbReports.length > 0 && (() => {
        const { freshest, visibleOlder, hiddenCount } = stackedProps(cbReports, 'CB');
        return (
          <div>
            <MobileCrystalBallCard
              key={freshest.id}
              worldId={worldId}
              playerProvinceId={provinceId}
              targetProvinceId={targetProvinceId}
              data={parseIntelData(freshest).data as CrystalBallData}
              report={freshest}
              isLoading={loading}
              error={error}
              onResult={onResult}
              onGathered={onGathered}
              onShare={() => handleShare(freshest.id)}
              onDelete={() => requestDelete(freshest.id)}
            />
            {visibleOlder.map((r) => (
              <MobileCrystalBallCard
                key={r.id}
                worldId={worldId}
                playerProvinceId={provinceId}
                targetProvinceId={targetProvinceId}
                data={parseIntelData(r).data as CrystalBallData}
                report={r}
                isLoading={loading}
                error={error}
                onResult={onResult}
                onGathered={onGathered}
                onShare={() => handleShare(r.id)}
                onDelete={() => requestDelete(r.id)}
                readOnly
              />
            ))}
            {showNMore('CB', hiddenCount)}
          </div>
        );
      })()}

      {somReports.length > 0 && (() => {
        const { freshest, visibleOlder, hiddenCount } = stackedProps(somReports, 'SOM');
        return (
          <div>
            <MobileSpyOnMilitaryCard
              key={freshest.id}
              worldId={worldId}
              playerProvinceId={provinceId}
              targetProvinceId={targetProvinceId}
              data={parseIntelData(freshest).data as SpyOnMilitaryData}
              report={freshest}
              isLoading={loading}
              error={error}
              onResult={onResult}
              onGathered={onGathered}
              onShare={() => handleShare(freshest.id)}
              onDelete={() => requestDelete(freshest.id)}
            />
            {visibleOlder.map((r) => (
              <MobileSpyOnMilitaryCard
                key={r.id}
                worldId={worldId}
                playerProvinceId={provinceId}
                targetProvinceId={targetProvinceId}
                data={parseIntelData(r).data as SpyOnMilitaryData}
                report={r}
                isLoading={loading}
                error={error}
                onResult={onResult}
                onGathered={onGathered}
                onShare={() => handleShare(r.id)}
                onDelete={() => requestDelete(r.id)}
                readOnly
              />
            ))}
            {showNMore('SOM', hiddenCount)}
          </div>
        );
      })()}

      {surveyReports.length > 0 && (() => {
        const { freshest, visibleOlder, hiddenCount } = stackedProps(surveyReports, 'SURVEY');
        return (
          <div>
            <MobileSurveyCard
              key={freshest.id}
              worldId={worldId}
              playerProvinceId={provinceId}
              targetProvinceId={targetProvinceId}
              data={parseIntelData(freshest).data as SurveyData}
              report={freshest}
              isLoading={loading}
              error={error}
              onResult={onResult}
              onGathered={onGathered}
              onShare={() => handleShare(freshest.id)}
              onDelete={() => requestDelete(freshest.id)}
            />
            {visibleOlder.map((r) => (
              <MobileSurveyCard
                key={r.id}
                worldId={worldId}
                playerProvinceId={provinceId}
                targetProvinceId={targetProvinceId}
                data={parseIntelData(r).data as SurveyData}
                report={r}
                isLoading={loading}
                error={error}
                onResult={onResult}
                onGathered={onGathered}
                onShare={() => handleShare(r.id)}
                onDelete={() => requestDelete(r.id)}
                readOnly
              />
            ))}
            {showNMore('SURVEY', hiddenCount)}
          </div>
        );
      })()}

      {infiltrateReports.map((r) => (
        <MobileInfiltrateCard
          key={r.id}
          data={parseIntelData(r).data as InfiltrateData}
          report={r}
          onShare={() => handleShare(r.id)}
          onDelete={() => requestDelete(r.id)}
        />
      ))}
      {sosReports.map((r) => (
        <MobileSpyOnScienceCard
          key={r.id}
          data={parseIntelData(r).data as SpyOnScienceData}
          report={r}
          onShare={() => handleShare(r.id)}
          onDelete={() => requestDelete(r.id)}
        />
      ))}
      {sohdReports.map((r) => (
        <MobileSpyOnHomeDefenseCard
          key={r.id}
          data={parseIntelData(r).data as SpyOnHomeDefenseData}
          report={r}
          onShare={() => handleShare(r.id)}
          onDelete={() => requestDelete(r.id)}
        />
      ))}
      {sotReports.map((r) => (
        <MobileSpyOnThroneCard
          key={r.id}
          data={parseIntelData(r).data as SpyOnThroneData}
          report={r}
          onShare={() => handleShare(r.id)}
          onDelete={() => requestDelete(r.id)}
        />
      ))}

      <ConfirmModal
        isOpen={pendingDeleteId != null}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
        title="Delete this report?"
        confirmText="Delete"
        cancelText="Cancel"
      >
        This can't be undone.
      </ConfirmModal>
    </div>
  );
}
